import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { api } from '../api';

const AmenityManagement = () => {
  const { user } = useContext(AuthContext);
  const [amenities, setAmenities] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState('types');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [hoaTrackSettings, setHoaTrackSettings] = useState({
    amenity_daily_limit_per_user: 2,
    amenity_auto_approval_enabled: true,
    amenity_require_board_approval: false
  });
  const [newAmenity, setNewAmenity] = useState({
    name: '',
    description: '',
    capacity: 1,
    hours_start: '07:00',
    hours_end: '22:00',
    always_open: false,
    block_minutes: 60,
    advance_booking_days: 14,
    min_booking_duration: 30,
    max_booking_duration: 240,
    rules: [],
    amenities_included: []
  });
  const [newReservation, setNewReservation] = useState({
    amenity_type: '',
    user_email: '',
    date: '',
    start_time: '',
    end_time: '',
    guest_count: 1
  });

  useEffect(() => {
    if (user?.hoa_id) {
      loadAmenities();
      loadReservations();
      loadHoaSettings();
    }
  }, [user?.hoa_id]);

  const loadAmenities = async () => {
    try {
      const response = await api.get('/api/amenities/types', {
        params: { hoa_id: user.hoa_id }
      });
      if (response.data.success) {
        setAmenities(response.data.amenity_types || []);
      }
    } catch (error) {
      console.error('Failed to load amenities:', error);
      // Fallback to setup default amenities if none exist
      if (error.response?.status === 404 || error.response?.status === 500) {
        await setupDefaultAmenities();
      }
    }
  };

  const loadHoaSettings = async () => {
    try {
      const { data } = await api.get('/api/settings/hoa-track', { params: { hoa_id: user.hoa_id } });
      if (data?.success && data.settings) {
        setHoaTrackSettings(prev => ({
          ...prev,
          amenity_daily_limit_per_user: data.settings.amenity_daily_limit_per_user ?? prev.amenity_daily_limit_per_user,
          amenity_auto_approval_enabled: data.settings.amenity_auto_approval_enabled ?? prev.amenity_auto_approval_enabled,
          amenity_require_board_approval: data.settings.amenity_require_board_approval ?? prev.amenity_require_board_approval,
        }));
      }
    } catch (e) {
      console.error('Failed to load HOA settings', e);
    }
  };

  const setupDefaultAmenities = async () => {
    try {
      await api.post(`/api/amenities/setup/${user.hoa_id}`);
      // Reload amenities after setup
      await loadAmenities();
    } catch (error) {
      console.error('Failed to setup default amenities:', error);
      // Use static fallback as last resort
      setAmenities([
        {
          id: '1',
          name: 'Recreation Room',
          capacity: 1,
          hours_start: '08:00',
          hours_end: '22:00',
          always_open: false,
          block_minutes: 30,
          description: 'Community recreation room'
        }
      ]);
    }
  };

  const loadReservations = async () => {
    try {
      const response = await api.get('/api/amenities/bookings', {
        params: { hoa_id: user.hoa_id }
      });
      setReservations(response.data.bookings || []);
    } catch (error) {
      console.error('Error loading reservations:', error);
      setReservations([]);
    }
  };

  const handleAddAmenity = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/amenities/types', newAmenity, {
        params: { hoa_id: user.hoa_id }
      });
      if (response.data.success) {
        await loadAmenities(); // Reload the list
        resetAmenityForm();
        setShowAddForm(false);
        alert('Amenity type added successfully!');
      }
    } catch (error) {
      console.error('Failed to add amenity:', error);
      alert('Failed to add amenity type. Please try again.');
    }
  };

  const resetAmenityForm = () => {
    setNewAmenity({
      name: '',
      description: '',
      capacity: 1,
      hours_start: '07:00',
      hours_end: '22:00',
      always_open: false,
      block_minutes: 60,
      advance_booking_days: 14,
      min_booking_duration: 30,
      max_booking_duration: 240,
      rules: [],
      amenities_included: []
    });
    setEditingAmenity(null);
  };

  const handleAddReservation = async (e) => {
    e.preventDefault();
    // This would save to backend
    const newId = Date.now().toString();
    setReservations(prev => [...prev, { 
      ...newReservation, 
      id: newId,
      user_name: 'Manual Entry',
      status: 'confirmed'
    }]);
    setNewReservation({
      amenity_type: '',
      user_email: '',
      date: '',
      start_time: '',
      end_time: '',
      guest_count: 1
    });
    setShowReservationForm(false);
    alert('Manual reservation added successfully!');
  };

  const deleteReservation = (reservationId) => {
    if (window.confirm('Are you sure you want to cancel this reservation?')) {
      setReservations(prev => prev.filter(r => r.id !== reservationId));
      alert('Reservation cancelled successfully!');
    }
  };

  const extendReservation = (reservationId) => {
    const newEndTime = prompt('Enter new end time (HH:MM):');
    if (newEndTime) {
      setReservations(prev => prev.map(r => 
        r.id === reservationId ? { ...r, end_time: newEndTime } : r
      ));
      alert('Reservation extended successfully!');
    }
  };

  const handleEditAmenity = (amenity) => {
    setEditingAmenity(amenity.id);
    setNewAmenity({
      name: amenity.name,
      description: amenity.description || '',
      capacity: amenity.capacity,
      hours_start: amenity.hours_start,
      hours_end: amenity.hours_end,
      always_open: amenity.always_open,
      block_minutes: amenity.block_minutes,
      advance_booking_days: amenity.advance_booking_days || 14,
      min_booking_duration: amenity.min_booking_duration || 30,
      max_booking_duration: amenity.max_booking_duration || 240,
      rules: amenity.rules || [],
      amenities_included: amenity.amenities_included || []
    });
    setShowAddForm(true);
  };

  const handleUpdateAmenity = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/api/amenities/types/${editingAmenity}`, newAmenity, {
        params: { hoa_id: user.hoa_id }
      });
      if (response.data.success) {
        await loadAmenities(); // Reload the list
        resetAmenityForm();
        setShowAddForm(false);
        alert('Amenity type updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update amenity:', error);
      alert('Failed to update amenity type. Please try again.');
    }
  };

  const handleDeleteAmenity = async (amenityId) => {
    if (window.confirm('Are you sure you want to delete this amenity type? This will disable it but preserve existing reservations.')) {
      try {
        const response = await api.delete(`/api/amenities/types/${amenityId}`, {
          params: { hoa_id: user.hoa_id }
        });
        if (response.data.success) {
          await loadAmenities(); // Reload the list
          alert('Amenity type deleted successfully!');
        }
      } catch (error) {
        console.error('Failed to delete amenity:', error);
        alert('Failed to delete amenity type. Please try again.');
      }
    }
  };

  const renderAmenitySettings = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900">Amenity Reservation Settings</h4>
      
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
        {/* Advance Booking Days Setting */}
        <div>
          <label className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-3">Advance Booking Limit:</span>
            <select className="px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
              <option value="7">7 days</option>
              <option value="14" selected>14 days</option>
              <option value="30">30 days</option>
            </select>
            <span className="text-sm text-gray-500 ml-2">in advance</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            How far in advance users can book amenities (time slots beyond this limit will not be available)
          </p>
        </div>

        {/* Daily Limit Setting */}
        <div>
          <label className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-3">Daily Reservation Limit per User:</span>
            <input
              type="number"
              min="1"
              max="10"
              value={hoaTrackSettings.amenity_daily_limit_per_user}
              onChange={(e) => setHoaTrackSettings(prev => ({ ...prev, amenity_daily_limit_per_user: parseInt(e.target.value) || 1 }))}
              className="w-20 px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-center"
            />
            <button
              onClick={async () => {
                try {
                  await api.post('/api/settings/hoa-track', {
                    amenity_daily_limit_per_user: hoaTrackSettings.amenity_daily_limit_per_user
                  }, { params: { hoa_id: user.hoa_id } });
                  alert('Daily limit saved');
                } catch (err) {
                  alert('Failed to save daily limit');
                }
              }}
              className="ml-3 px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Save
            </button>
            <span className="text-sm text-gray-500 ml-2">reservations per day</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Maximum number of amenity reservations a single user can make per day
          </p>
        </div>

        {/* Clubhouse Setting */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={!!hoaTrackSettings.has_clubhouse}
              onChange={(e) => setHoaTrackSettings(prev => ({ ...prev, has_clubhouse: e.target.checked }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">
              Our HOA has a clubhouse
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-7">
            Enable this to show clubhouse hours in the Neighborhood tab
          </p>
        </div>

        {/* Auto-Approval Setting */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={!!hoaTrackSettings.amenity_auto_approval_enabled}
              onChange={(e) => setHoaTrackSettings(prev => ({ ...prev, amenity_auto_approval_enabled: e.target.checked }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">
              Auto-approve amenity reservations
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-7">
            Automatically approve amenity reservations without board review
          </p>
        </div>

        {/* Board Approval Required Setting */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={!!hoaTrackSettings.amenity_require_board_approval}
              onChange={(e) => setHoaTrackSettings(prev => ({ ...prev, amenity_require_board_approval: e.target.checked }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">
              Require board approval for all amenity reservations
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-7">
            All amenity reservations will require explicit board approval
          </p>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t">
          <button
            onClick={async () => {
              try {
                await api.post('/api/settings/hoa-track', hoaTrackSettings, { params: { hoa_id: user.hoa_id } });
                alert('Amenity settings saved');
              } catch (e) {
                console.error('Failed to save amenity settings', e);
                alert('Failed to save amenity settings');
              }
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            💾 Save Settings
          </button>
        </div>
      </div>
    </div>
  );

  const renderAmenityTypes = () => (
    <div className="space-y-6">
      {/* Add Button */}
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-900">Amenity Types</h4>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          + Add Amenity Type
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h5 className="text-md font-medium text-gray-900 mb-4">
            {editingAmenity ? 'Edit Amenity Type' : 'Add New Amenity Type'}
          </h5>
          <form onSubmit={editingAmenity ? handleUpdateAmenity : handleAddAmenity} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenity Name</label>
                <input
                  type="text"
                  required
                  value={newAmenity.name}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Recreation Room, Swimming Pool"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newAmenity.description}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Brief description of the amenity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={newAmenity.capacity}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours Start</label>
                <input
                  type="time"
                  value={newAmenity.hours_start}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, hours_start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={newAmenity.always_open}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours End</label>
                <input
                  type="time"
                  value={newAmenity.hours_end}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, hours_end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={newAmenity.always_open}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Block (minutes)</label>
                <select
                  value={newAmenity.block_minutes}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, block_minutes: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Advance Booking Limit (days)</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={newAmenity.advance_booking_days}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, advance_booking_days: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">How far in advance this specific amenity can be booked</p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="always_open"
                  checked={newAmenity.always_open}
                  onChange={(e) => setNewAmenity(prev => ({ ...prev, always_open: e.target.checked }))}
                  className="mr-2 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="always_open" className="text-sm text-gray-700">24/7 Access</label>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                {editingAmenity ? 'Update Amenity' : 'Add Amenity'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  resetAmenityForm();
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Amenities List */}
      <div className="grid gap-4">
        {amenities.map(amenity => (
          <div key={amenity.id} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <h5 className="text-lg font-medium text-gray-900">{amenity.name}</h5>
                  {amenity.block_minutes === 30 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      30-min slots
                    </span>
                  )}
                </div>
                {amenity.description && (
                  <p className="mt-1 text-sm text-gray-500">{amenity.description}</p>
                )}
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <div>Capacity: {amenity.capacity} {amenity.capacity === 1 ? 'reservation' : 'reservations'} per slot</div>
                  <div>
                    Hours: {amenity.always_open ? '24/7 Access' : `${amenity.hours_start} - ${amenity.hours_end}`}
                  </div>
                  <div>Time Blocks: {amenity.block_minutes} minutes</div>
                  <div>Advance Booking: {amenity.advance_booking_days || 14} days</div>
                  {amenity.rules && amenity.rules.length > 0 && (
                    <div>
                      <span className="font-medium">Rules:</span>
                      <ul className="list-disc list-inside ml-2 text-xs">
                        {amenity.rules.slice(0, 2).map((rule, index) => (
                          <li key={index}>{rule}</li>
                        ))}
                        {amenity.rules.length > 2 && <li>+{amenity.rules.length - 2} more...</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => handleEditAmenity(amenity)}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteAmenity(amenity.id)}
                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderReservationTracker = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-900">Current Reservations</h4>
        <button
          onClick={() => setShowReservationForm(!showReservationForm)}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          + Manual Reservation
        </button>
      </div>

      {/* Manual Reservation Form */}
      {showReservationForm && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h5 className="text-md font-medium text-gray-900 mb-4">Add Manual Reservation</h5>
          <form onSubmit={handleAddReservation} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amenity</label>
              <select
                required
                value={newReservation.amenity_type}
                onChange={(e) => setNewReservation(prev => ({ ...prev, amenity_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Amenity</option>
                {amenities.map(amenity => (
                  <option key={amenity.id} value={amenity.name}>{amenity.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
              <input
                type="email"
                required
                value={newReservation.user_email}
                onChange={(e) => setNewReservation(prev => ({ ...prev, user_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                required
                value={newReservation.date}
                onChange={(e) => setNewReservation(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guest Count</label>
              <input
                type="number"
                min="1"
                required
                value={newReservation.guest_count}
                onChange={(e) => setNewReservation(prev => ({ ...prev, guest_count: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="time"
                required
                value={newReservation.start_time}
                onChange={(e) => setNewReservation(prev => ({ ...prev, start_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="time"
                required
                value={newReservation.end_time}
                onChange={(e) => setNewReservation(prev => ({ ...prev, end_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Add Reservation
              </button>
              <button
                type="button"
                onClick={() => setShowReservationForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reservations List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amenity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{reservation.amenity_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{reservation.user_name}</div>
                    <div className="text-sm text-gray-500">{reservation.user_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{new Date(reservation.date).toLocaleDateString()}</div>
                    <div>{reservation.start_time} - {reservation.end_time}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {reservation.guest_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {reservation.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => extendReservation(reservation.id)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Extend
                    </button>
                    <button
                      onClick={() => deleteReservation(reservation.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {reservations.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🏊‍♂️</div>
            <p>No current reservations</p>
            <p className="text-sm mt-1">Approved amenity reservations will appear here</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Amenity Management</h3>
        <p className="mt-1 text-sm text-gray-600">
          Configure amenity types and manage current reservations.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('types')}
            className={`${
              activeTab === 'types'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            🏊‍♂️ Amenity Types
          </button>
          <button
            onClick={() => setActiveTab('tracker')}
            className={`${
              activeTab === 'tracker'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            📅 Reservation Tracker
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`${
              activeTab === 'settings'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            ⚙️ Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'types' && renderAmenityTypes()}
      {activeTab === 'tracker' && renderReservationTracker()}
      {activeTab === 'settings' && renderAmenitySettings()}
    </div>
  );
};

export default AmenityManagement;